import type { Recipe, StockItem, UnitOfMeasure } from '../types'
import { recipeQuantity } from './inventory-format'

/**
 * What a recipe holds, as the editor compares it: the saved version on one side,
 * the form on the other.
 *
 * A quantity can be `undefined` on the form side — a cleared number field — and
 * a line can have no ingredient yet. Neither is a change worth listing: the form
 * refuses to save them anyway, and saying "Milk: 200 ml → ?" helps nobody.
 */
export interface RecipeSnapshot {
  name: string
  yieldQty: number | undefined
  yieldUnit: UnitOfMeasure
  lines: { stockItemId: string; qty: number | undefined; unitOfMeasure: UnitOfMeasure }[]
}

/** One difference between the saved version and the form. */
export interface RecipeChange {
  key: string
  kind: 'added' | 'removed' | 'changed'
  label: string
  /** How it reads in the saved version. Absent for something added. */
  from?: string
  /** How it will read once saved. Absent for something removed. */
  to?: string
}

/**
 * The snapshot a recipe starts from. With no recipe — one about to be created —
 * that is what the form opens with: the item's name, one of its unit, no lines.
 * Comparing against it lists everything the new recipe will hold.
 */
export function snapshotOf(recipe: Recipe | null, item: StockItem): RecipeSnapshot {
  if (!recipe) {
    return { name: item.name, yieldQty: 1, yieldUnit: item.unitOfMeasure, lines: [] }
  }

  return {
    name: recipe.name,
    yieldQty: recipe.yieldQty,
    yieldUnit: recipe.yieldUnit,
    lines: recipe.lines.map((line) => ({
      stockItemId: line.itemId,
      qty: line.qty,
      unitOfMeasure: line.unitOfMeasure,
    })),
  }
}

function amount(qty: number | undefined, unit: UnitOfMeasure): string {
  return qty === undefined || !Number.isFinite(qty) ? `? ${unit}` : `${recipeQuantity(qty)} ${unit}`
}

/**
 * Everything the next save would change, in reading order: the recipe's own
 * fields, then its ingredients in the order the form lists them, then the ones
 * taken out, then whether the order itself moved.
 *
 * `names` turns an ingredient id into something a person recognises; an id it
 * does not know yet reads as "An ingredient" until its item loads.
 */
export function diffRecipe(
  saved: RecipeSnapshot,
  draft: RecipeSnapshot,
  names: ReadonlyMap<string, string>,
): RecipeChange[] {
  const changes: RecipeChange[] = []
  const nameOf = (id: string) => names.get(id) ?? 'An ingredient'

  const draftName = draft.name.trim()
  if (draftName && draftName !== saved.name) {
    changes.push({ key: 'name', kind: 'changed', label: 'Name', from: saved.name, to: draftName })
  }

  if (
    draft.yieldQty !== undefined &&
    Number.isFinite(draft.yieldQty) &&
    (draft.yieldQty !== saved.yieldQty || draft.yieldUnit !== saved.yieldUnit)
  ) {
    changes.push({
      key: 'yield',
      kind: 'changed',
      label: 'Makes',
      from: amount(saved.yieldQty, saved.yieldUnit),
      to: amount(draft.yieldQty, draft.yieldUnit),
    })
  }

  const savedById = new Map(saved.lines.map((line) => [line.stockItemId, line]))
  // Only lines with an ingredient, and each ingredient once: a duplicate is a
  // validation error, not a second change.
  const draftLines = draft.lines.filter(
    (line, index, all) =>
      line.stockItemId &&
      all.findIndex((other) => other.stockItemId === line.stockItemId) === index,
  )
  const draftIds = new Set(draftLines.map((line) => line.stockItemId))

  for (const line of draftLines) {
    const before = savedById.get(line.stockItemId)

    if (!before) {
      changes.push({
        key: `add:${line.stockItemId}`,
        kind: 'added',
        label: nameOf(line.stockItemId),
        to: amount(line.qty, line.unitOfMeasure),
      })
    } else if (
      line.qty !== undefined &&
      Number.isFinite(line.qty) &&
      (line.qty !== before.qty || line.unitOfMeasure !== before.unitOfMeasure)
    ) {
      changes.push({
        key: `edit:${line.stockItemId}`,
        kind: 'changed',
        label: nameOf(line.stockItemId),
        from: amount(before.qty, before.unitOfMeasure),
        to: amount(line.qty, line.unitOfMeasure),
      })
    }
  }

  for (const line of saved.lines) {
    if (!draftIds.has(line.stockItemId)) {
      changes.push({
        key: `remove:${line.stockItemId}`,
        kind: 'removed',
        label: nameOf(line.stockItemId),
        from: amount(line.qty, line.unitOfMeasure),
      })
    }
  }

  // The lines are saved in the order shown, so a reorder is a change of its own
  // — but only among the ingredients both sides share, or every addition would
  // read as a reorder too.
  const keptInSavedOrder = saved.lines
    .map((line) => line.stockItemId)
    .filter((id) => draftIds.has(id))
  const keptInDraftOrder = draftLines
    .map((line) => line.stockItemId)
    .filter((id) => savedById.has(id))
  if (keptInSavedOrder.some((id, index) => keptInDraftOrder[index] !== id)) {
    changes.push({ key: 'order', kind: 'changed', label: 'Ingredient order', to: 'Rearranged' })
  }

  return changes
}
